import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import Footer from '../components/Footer';
import Navigation from '../components/Navigation';
import {
  ArrowRight, Phone, CheckCircle, Star, Quote, ShieldCheck, Ear, ClipboardList,
  Clock, BookOpen, MessageCircle, Compass, LineChart, Loader2, Send, Award,
  Zap, Heart, Shield, TrendingUp, UserPlus, Sparkles, GraduationCap,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP = 'http://wa.me/+918291188188';
// Brand palette matched to dacharya.com — teal primary, orange accent
const TEAL = '#0d6d64';
const TEAL_DARK = '#0a544d';
const CREAM = '#F7F1E4';

const OVERCOMES = [
  { title: 'Screen Addiction & Digital Fatigue', desc: 'Breaking the cycle of constant digital stimulation and re-engaging with the real world.', icon: Zap },
  { title: 'Academic Pressure & Exam Stress', desc: 'Managing the weight of expectations and finding joy in learning again.', icon: BookOpen },
  { title: 'Social Isolation & Communication Gaps', desc: 'Overcoming the barriers that prevent meaningful connection with peers and family.', icon: MessageCircle },
  { title: 'Unclear Career/Interest Direction', desc: 'Navigating the confusion of early pre-teen choices with expert clarity.', icon: Compass },
];

const RECEIVES = [
  { title: "Deep Understanding of Their Child's Inner World", desc: 'Accessing the thoughts and feelings that are often left unsaid during daily routines.', icon: Ear },
  { title: 'Clear Insights Into Hidden Behavioral Triggers', desc: 'Scientific analysis that decodes why your child reacts to specific situations.', icon: LineChart },
  { title: 'Expert Guidance on Navigating Conflicts', desc: 'Practical tools and communication strategies to resolve friction and build trust.', icon: ShieldCheck },
  { title: 'A Scientific Plan for Child Development', desc: "A personalized, data-backed roadmap tailored to your child's unique strengths.", icon: ClipboardList },
];

const TESTIMONIALS = [
  { quote: "My daughter used to keep her feelings bottled up. After 2 months with Sanjaya, she's talking about her school day with such clarity.", name: 'Ananya', role: 'Parent of 7yo' },
  { quote: 'The reports are so insightful. I finally understand the specific anxieties my son was facing in class. It\'s been a game changer.', name: 'Rohan K.', role: 'Parent of 9yo' },
  { quote: 'The 5-minute call is the highlight of my child\'s day. He feels like a VIP. His confidence has grown tremendously.', name: 'Sunita L.', role: 'Parent of 11yo' },
  { quote: 'I was skeptical at first, but the weekly insights showed patterns in my son\'s behaviour I would never have noticed on my own. It felt like having a child psychologist on speed dial.', name: 'Meera D.', role: 'Parent of 8yo' },
  { quote: 'What surprised me most is how gentle and unhurried the calls feel. My daughter now looks forward to them the way she looks forward to her favourite show.', name: 'Vikram S.', role: 'Parent of 10yo' },
  { quote: 'The principal\'s personal review of every report is what sold me. It doesn\'t feel like an app — it feels like a school that genuinely cares.', name: 'Priya R.', role: 'Parent of 12yo' },
];

const STATS = [
  { label: 'SESSIONS MONTHLY', value: '5,000+' },
  { label: 'CONFIDENCE BOOST', value: '92%' },
  { label: 'PARENT CONNECTION', value: '98%' },
  { label: 'DAILY INSIGHTS', value: '15,000+' },
];

const Home = () => {
  const { toast } = useToast();
  const location = useLocation();
  const [form, setForm] = useState({ name: '', phone: '', message: '', parent_role: 'Mother', child_age: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Lets the "Enroll Now" nav button (Link to="/#begin-trial") jump straight to the
  // registration section — runs on every hash change, not just first mount, so it
  // still works when clicked while already on the Home page.
  useEffect(() => {
    if (location.hash === '#begin-trial') {
      const el = document.getElementById('begin-trial');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);
  // Standalone plan-registration gate — independent of the /enroll account flow.
  // Once this browser has submitted the quick registration form below, the two
  // plan blocks stay unlocked on every future visit.
  const [hasRegistered, setHasRegistered] = useState(() => !!localStorage.getItem('sanjaya_plan_inquiry_submitted'));
  const [planReg, setPlanReg] = useState({ parent_name: '', email: '', phone: '', child_name: '', child_age: '', school_name: '' });
  const [planRegSubmitting, setPlanRegSubmitting] = useState(false);

  const submitPlanRegistration = async (e) => {
    e.preventDefault();
    const { parent_name, email, phone, child_name, child_age } = planReg;
    if (!parent_name.trim() || !email.trim() || !phone.trim() || !child_name.trim() || !child_age) {
      toast({ title: 'Please fill in parent name, email, phone, child name and age.', variant: 'destructive' });
      return;
    }
    setPlanRegSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/inquiries`, {
        parent_name: parent_name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        child_name: child_name.trim(),
        child_age: parseInt(child_age, 10) || 0,
        school_name: planReg.school_name.trim(),
        message: 'Registered from the Transparent Plans section to view pricing.',
      });
      localStorage.setItem('sanjaya_plan_inquiry_submitted', 'true');
      setHasRegistered(true);
      toast({ title: 'Registered!', description: 'Our team has your details — here are the plans.' });
    } catch (err) {
      toast({ title: 'Could not submit registration. Please try WhatsApp instead.', variant: 'destructive' });
    } finally {
      setPlanRegSubmitting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast({ title: 'Please share your name and WhatsApp number.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/inquiries`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        message: `Parent role: ${form.parent_role}. Child's age: ${form.child_age || 'not specified'}. ${form.message || ''}`.trim(),
      });
      setSubmitted(true);
      toast({ title: 'Thanks — we\'ll be in touch within 24 hours.' });
    } catch (err) {
      toast({ title: 'Could not send inquiry. Please try WhatsApp.', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: CREAM, fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' }}>
      <Navigation />

      {/* ============ HERO ============ */}
      <section className="pt-16 sm:pt-24 pb-16 sm:pb-24 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 h-9" style={{ background: '#e7f4f2', color: TEAL }} data-testid="hero-eyebrow">
                <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                <span className="text-[11px] tracking-[0.2em] font-semibold uppercase">Principal-Led Excellence</span>
              </div>
              <div className="inline-flex items-center gap-2 bg-white border border-gray-200 shadow-sm rounded-full px-4 py-2 h-9">
                <img
                  src="/images/skill-bharat-logo.jpg.png"
                  alt="Skill Bharat Association"
                  className="h-5 w-5 object-contain flex-shrink-0"
                />
                <span className="text-xs font-medium text-gray-600">Supported by <span className="font-semibold text-gray-800">Skill Bharat Association</span></span>
              </div>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05]" style={{ fontFamily: 'Georgia, "Playfair Display", serif' }} data-testid="hero-title">
              <span style={{ color: '#1a2340' }}>Nurturing Every</span><br />
              <span style={{ color: TEAL }}>Child&rsquo;s Voice</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              We provide a safe, structured space for children to be truly heard, helping them build emotional intelligence and confidence through expert listening.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/contact">
                <Button size="lg" className="text-white rounded-full px-8 gap-2 tracking-wider text-sm font-semibold h-12" style={{ background: 'linear-gradient(90deg,#f39c12,#e67e22)' }} data-testid="hero-start-journey-btn">
                  START JOURNEY <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/plans">
                <Button variant="outline" size="lg" className="rounded-full px-8 tracking-wider text-sm font-semibold h-12 border-2 hover:text-white" style={{ borderColor: TEAL, color: TEAL, background: 'transparent' }} data-testid="hero-view-programs-btn">
                  VIEW PROGRAMS
                </Button>
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img src="https://dacharya.com/wp-content/uploads/2026/05/child-on-call-723x1024.jpg" alt="Child on a Sanjaya call" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="absolute top-6 -left-4 sm:-left-8 bg-white rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3 max-w-[240px]">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#fff4e5' }}>
                <Clock className="w-5 h-5" style={{ color: '#e67e22' }} />
              </div>
              <div>
                <div className="text-sm font-bold text-slate-900">5-Min Daily Listening</div>
                <div className="text-xs text-slate-500">Consistent emotional check-ins</div>
              </div>
            </div>
            <div className="absolute -bottom-4 right-2 sm:-right-8 bg-white rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3 max-w-[240px]">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#e7f4f2' }}>
                <LineChart className="w-5 h-5" style={{ color: TEAL }} />
              </div>
              <div>
                <div className="text-sm font-bold text-slate-900">Principal-Guided Reports</div>
                <div className="text-xs text-slate-500">Deep pedagogical insights</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOUNDER ============ */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-2 order-2 lg:order-1">
            <div className="rounded-[2rem] overflow-hidden bg-gradient-to-br from-amber-100 to-rose-100 aspect-[4/5] max-w-xs mx-auto lg:mx-0">
              <img src="https://customer-assets.emergentagent.com/job_912f38ca-962a-42a7-9f88-0f9aebc0953f/artifacts/i0ck2cs3_image.png" alt="Smt. Punam Jaiswal" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
          <div className="lg:col-span-3 order-1 lg:order-2">
            <p className="text-lg font-semibold mb-2" style={{ color: TEAL }}>The Visionary Behind Sanjaya</p>
            <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Georgia, serif', color: TEAL }}>
              Founder &amp; Former Principal :- Smt. Punam Jaiswal
            </h2>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full px-4 py-2" style={{ background: '#e7f4f2', color: TEAL }}>
              <Award className="w-4 h-4" />
              <span className="text-sm font-semibold">35+ Years of Experience in Education and Child Psychology</span>
            </div>
            <blockquote className="mt-6 relative pl-6 border-l-4 py-2" style={{ borderColor: '#e67e22' }}>
              <p className="text-xl text-slate-800 leading-relaxed italic" style={{ fontFamily: 'Georgia, serif' }}>
                &ldquo;Every child has a story to tell. My role is simply to listen, understand, and help parents see the beautiful complexity of their child&rsquo;s world.&rdquo;
              </p>
            </blockquote>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Smt. Punam Jaiswal has dedicated her life to understanding the intersection of academic pressure and child emotional growth. Her unique observation-first approach has transformed thousands of parent-child relationships.
            </p>
          </div>
        </div>
      </section>

      {/* ============ THE LISTENING PROMISE ============ */}
      <section className="py-20 px-4" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-xs tracking-[0.3em] font-bold uppercase mb-3" style={{ color: TEAL }}>THE LISTENING PROMISE</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold" style={{ fontFamily: 'Georgia, serif', color: '#1a2340' }}>
            A 5-Minute Daily Call of Listening
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            We provide a safe space for students to share their day, thoughts, and feelings. This simple act of consistency builds profound emotional resilience.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <span className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 text-sm font-semibold shadow-sm" style={{ color: TEAL }}>
              <CheckCircle className="w-4 h-4" /> Not Counselling
            </span>
            <span className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 text-sm font-semibold shadow-sm" style={{ color: TEAL }}>
              <CheckCircle className="w-4 h-4" /> Not Teaching
            </span>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { title: 'Safe Expression', desc: 'A private space where children speak freely about their day, thoughts, and emotions without the fear of being corrected.', icon: Shield },
            { title: 'Consistent Listening', desc: "Daily 5-minute sessions that build a habit of communication and reinforce the child's sense of self-worth.", icon: Ear },
            { title: 'Meaningful Insights', desc: "Observations translated into actionable feedback for parents to understand their child's inner world better.", icon: ClipboardList },
          ].map((it) => {
            const Icon = it.icon;
            return (
              <div key={it.title} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ background: '#e7f4f2' }}>
                  <Icon className="w-7 h-7" style={{ color: TEAL }} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Georgia, serif' }}>{it.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{it.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ============ 3-STEP PROCESS ============ */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold" style={{ fontFamily: 'Georgia, serif', color: TEAL }}>A Professional 3-Step Process</h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">Our principal-led methodology ensures every interaction is measured, meaningful, and growth-oriented.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: '01.', title: 'Setup', desc: 'Initial assessment and matching with a dedicated wellness observer based on child profile.' },
              { n: '02.', title: 'Analysis', desc: 'Daily 5-minute sessions translated into weekly wellness insights for parents and educators.' },
              { n: '03.', title: 'Growth', desc: 'Long-term behavioral tracking and emotional maturity milestones reached through consistent support.' },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl p-8 border border-slate-100 hover:shadow-md transition-shadow" style={{ background: CREAM }}>
                <div className="text-4xl font-bold" style={{ fontFamily: 'Georgia, serif', color: '#e67e22' }}>{s.n} <span style={{ color: TEAL }}>{s.title}</span></div>
                <p className="mt-4 text-slate-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TRANSFORMATION ============ */}
      <section className="py-20 px-4" style={{ background: CREAM }}>
        <style>{`
          .transformation-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1rem;
          }
          @media (min-width: 1024px) {
            .transformation-grid {
              grid-template-columns: repeat(2, 1fr);
              grid-auto-flow: column;
              grid-template-rows: repeat(5, auto);
              column-gap: 2rem;
              row-gap: 1.25rem;
            }
          }
        `}</style>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold" style={{ fontFamily: 'Georgia, serif', color: TEAL }}>The Sanjaya Transformation</h2>
            <p className="mt-3 text-slate-600 max-w-2xl mx-auto">Bridging the Gap to Happy, Confident Children</p>
          </div>
          {/*
            A single grid (not two independent columns) so each "Overcome" card
            and its matching "Receive" card share a grid row and stretch to the
            same height — titles of different lengths no longer leave one
            column taller than the other. Falls back to a simple stacked list
            on mobile.
          */}
          <div className="transformation-grid">
            <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Georgia, serif', color: '#1a2340' }}>We Help Children Overcome</h3>
            {OVERCOMES.map((it) => {
              const Icon = it.icon;
              return (
                <div
                  key={it.title}
                  className="flex items-start gap-4 p-5 rounded-xl bg-white shadow-sm border border-transparent hover:border-rose-100 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#fdecea' }}>
                    <Icon className="w-5 h-5" style={{ color: '#e74c3c' }} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900" style={{ fontFamily: 'Georgia, serif' }}>{it.title}</h4>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">{it.desc}</p>
                  </div>
                </div>
              );
            })}
            <h3 className="text-2xl font-bold mb-2 mt-2 lg:mt-0" style={{ fontFamily: 'Georgia, serif', color: '#1a2340' }}>What Parents Receive</h3>
            {RECEIVES.map((it) => {
              const Icon = it.icon;
              return (
                <div
                  key={it.title}
                  className="flex items-start gap-4 p-5 rounded-xl bg-white shadow-sm border border-transparent hover:border-teal-100 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#e7f4f2' }}>
                    <Icon className="w-5 h-5" style={{ color: TEAL }} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900" style={{ fontFamily: 'Georgia, serif' }}>{it.title}</h4>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">{it.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ 3 IMAGE CARDS ============ */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { img: 'https://dacharya.com/wp-content/uploads/2026/05/Child-Interaction-with-Our-Observer.png', title: 'Child Interaction with Our Observer', desc: 'Dedicated one-on-one sessions where your child speaks freely to a trained specialist who knows how to unlock their potential.' },
            { img: 'https://dacharya.com/wp-content/uploads/2026/05/Principal-Monitoring.png', title: 'Principal Monitoring', desc: 'Every interaction is audited by Smt. Punam Jaiswal personally, ensuring no nuance is missed in the assessment.' },
            { img: 'https://dacharya.com/wp-content/uploads/2026/05/child-on-call-723x1024.jpg', title: 'Parent Guidance', desc: 'Actionable insights and structured guidance delivered directly to parents, so every day builds toward growth.' },
          ].map((c) => (
            <div key={c.title} className="rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow" style={{ background: CREAM }}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={c.img} alt={c.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-6">
                <h3 className="font-bold text-lg" style={{ fontFamily: 'Georgia, serif', color: TEAL }}>{c.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ STATS ============ */}
      <section className="py-16 px-4" style={{ background: TEAL }}>
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="text-4xl sm:text-5xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>{s.value}</div>
              <div className="mt-2 text-[11px] tracking-[0.2em] font-semibold uppercase opacity-90">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ PRICING ============ */}
      <section id="begin-trial" className="py-20 px-4 bg-white scroll-mt-20">
        <div className="max-w-5xl mx-auto text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold" style={{ fontFamily: 'Georgia, serif', color: TEAL }}>Begin the Journey With a Trial</h2>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">Invest in your child&rsquo;s emotional architecture with our structured programs.</p>
        </div>
        {!hasRegistered ? (
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center" data-testid="pricing-register-gate">
            {/* LEFT: narrative + a little animated life, mirrors the Inquiry section's layout */}
            <div className="text-center md:text-left">
              <div className="relative max-w-[280px] mx-auto md:mx-0 mb-8">
                <div className="rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1457530378978-8bac673b8062?w=800&q=80&auto=format&fit=crop"
                    alt="Young seedlings growing in bright light, symbolizing nurturing and growth"
                    className="w-full h-56 object-cover"
                    loading="lazy"
                  />
                </div>
                <div
                  className="absolute -top-4 -right-4 w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg,#0d6d64,#1a8f80)', boxShadow: '0 12px 30px -8px rgba(13,109,100,0.5)' }}
                >
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div
                  className="absolute -bottom-3 -left-3 w-10 h-10 rounded-xl bg-white shadow-md flex items-center justify-center animate-bounce"
                  style={{ animationDuration: '3s' }}
                >
                  <Heart className="w-4 h-4 text-rose-500" />
                </div>
                <div
                  className="absolute top-1/2 -right-3 w-9 h-9 rounded-xl bg-white shadow-md flex items-center justify-center animate-bounce"
                  style={{ animationDuration: '2.5s', animationDelay: '0.4s' }}
                >
                  <GraduationCap className="w-4 h-4" style={{ color: '#e67e22' }} />
                </div>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Georgia, serif', color: '#1a2340' }}>
                Let&rsquo;s get to know your family
              </h3>
              <p className="mt-3 text-slate-600 leading-relaxed">
                A few quick details help us set up the right plan and start your child&rsquo;s free trial session.
              </p>
              <ul className="mt-6 space-y-2.5 inline-block text-left">
                {['Free trial, no card needed', 'Takes under 2 minutes', 'A Principal reviews every report'].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: TEAL }} /> {t}
                  </li>
                ))}
              </ul>
              <p className="mt-6 flex items-center justify-center md:justify-start gap-1.5 text-xs text-slate-400">
                <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" style={{ color: TEAL }} />
                Your details are safe with us — used only to set up your child&rsquo;s trial.
              </p>
            </div>

            {/* RIGHT: the form card */}
            <div
              className="rounded-3xl overflow-hidden bg-white"
              style={{ boxShadow: '0 20px 45px -12px rgba(13,109,100,0.18)', border: '1px solid #e7f4f2' }}
            >
              <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg,#0d6d64,#22a894,#F0A820)' }} />
              <div className="p-6 sm:p-8">
                <form onSubmit={submitPlanRegistration} className="space-y-4" data-testid="plan-register-form">
                  <div>
                    <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Parent Name <span style={{ color: '#e67e22' }}>*</span></label>
                    <Input placeholder="Your name" value={planReg.parent_name} onChange={(e) => setPlanReg({ ...planReg, parent_name: e.target.value })} className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-teal-500/30 focus-visible:border-teal-400" data-testid="plan-reg-parent-name" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Child&rsquo;s Name <span style={{ color: '#e67e22' }}>*</span></label>
                    <Input placeholder="Child's name" value={planReg.child_name} onChange={(e) => setPlanReg({ ...planReg, child_name: e.target.value })} className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-teal-500/30 focus-visible:border-teal-400" data-testid="plan-reg-child-name" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Email <span style={{ color: '#e67e22' }}>*</span></label>
                    <Input type="email" placeholder="you@email.com" value={planReg.email} onChange={(e) => setPlanReg({ ...planReg, email: e.target.value })} className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-teal-500/30 focus-visible:border-teal-400" data-testid="plan-reg-email" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">WhatsApp Number <span style={{ color: '#e67e22' }}>*</span></label>
                    <Input placeholder="+91" value={planReg.phone} onChange={(e) => setPlanReg({ ...planReg, phone: e.target.value })} className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-teal-500/30 focus-visible:border-teal-400" data-testid="plan-reg-phone" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Child&rsquo;s Age <span style={{ color: '#e67e22' }}>*</span></label>
                      <select value={planReg.child_age} onChange={(e) => setPlanReg({ ...planReg, child_age: e.target.value })} className="mt-1.5 w-full h-11 border border-slate-200 rounded-xl px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400" data-testid="plan-reg-child-age">
                        <option value="">Select</option>
                        {[5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map((a) => (<option key={a} value={a}>{a}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">School</label>
                      <Input placeholder="School name" value={planReg.school_name} onChange={(e) => setPlanReg({ ...planReg, school_name: e.target.value })} className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-teal-500/30 focus-visible:border-teal-400" data-testid="plan-reg-school" />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={planRegSubmitting}
                    className="w-full text-white rounded-full tracking-wider text-sm font-semibold h-12 gap-2 shadow-md hover:shadow-lg transition-shadow"
                    style={{ background: 'linear-gradient(90deg,#0d6d64,#159585)' }}
                    data-testid="pricing-register-now-btn"
                  >
                    {planRegSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                    REGISTER NOW
                  </Button>
                </form>
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border-2 rounded-3xl p-8" style={{ borderColor: '#e7f4f2' }}>
              <div className="text-[10px] tracking-[0.25em] font-semibold text-slate-500 uppercase mb-3">FLEXIBLE ENTRY</div>
              <h3 className="text-3xl font-bold" style={{ fontFamily: 'Georgia, serif', color: TEAL }}>Monthly</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold" style={{ fontFamily: 'Georgia, serif', color: '#1a2340' }}>Rs. 17,999</span>
                <span className="text-slate-500">/month</span>
              </div>
              <ul className="mt-6 space-y-3 text-sm text-slate-700">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: TEAL }} /> Daily 5-Min Wellness Call</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: TEAL }} /> Daily Insight Reports</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: TEAL }} /> Direct Principal Access</li>
              </ul>
              <a href={WHATSAPP} target="_blank" rel="noopener noreferrer">
                <Button className="mt-8 w-full text-white rounded-full tracking-wider text-sm font-semibold" style={{ background: TEAL }} data-testid="pricing-monthly-btn">START FREE TRIAL</Button>
              </a>
            </div>
            <div className="rounded-3xl p-8 relative shadow-xl text-white" style={{ background: 'linear-gradient(135deg,#f39c12,#e67e22)' }}>
              <div className="text-[10px] tracking-[0.25em] font-semibold uppercase mb-3 opacity-90">TRANSFORMATIONAL</div>
              <h3 className="text-3xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>Quarterly</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>Rs 49,999</span>
                <span className="opacity-90">/3 month</span>
              </div>
              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> All Monthly Features</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> Personalized Growth Roadmap</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> Quarterly Goal-Setting Session</li>
              </ul>
              <a href={WHATSAPP} target="_blank" rel="noopener noreferrer">
                <Button className="mt-8 w-full bg-white hover:bg-orange-50 rounded-full tracking-wider text-sm font-semibold" style={{ color: '#e67e22' }} data-testid="pricing-quarterly-btn">CHOOSE GROWTH</Button>
              </a>
            </div>
          </div>
        )}
      </section>

      {/* ============ TESTIMONIALS (auto-scrolling marquee) ============ */}
      <section className="py-20 px-4 overflow-hidden" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold" style={{ fontFamily: 'Georgia, serif', color: TEAL }}>Voices of Parents</h2>
          <p className="mt-3 text-slate-600">Real stories from the Parents.</p>
        </div>
        <div
          className="testimonials-marquee relative max-w-6xl mx-auto"
          data-testid="testimonials-marquee"
        >
          <div className="testimonials-track flex gap-6 w-max">
            {[...TESTIMONIALS, ...TESTIMONIALS, ...TESTIMONIALS].map((t, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-6 shadow-sm w-[320px] sm:w-[360px] flex-shrink-0"
                data-testid={`testimonial-card-${idx}`}
              >
                <Quote className="w-8 h-8 mb-3" style={{ color: TEAL, opacity: 0.4 }} />
                <p className="text-slate-700 leading-relaxed italic" style={{ fontFamily: 'Georgia, serif' }}>{t.quote}</p>
                <div className="mt-6 pt-4 border-t border-slate-100 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold" style={{ background: TEAL }}>{t.name.charAt(0)}</div>
                  <div>
                    <div className="font-semibold text-slate-900">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ INQUIRY FORM ============ */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Georgia, serif', color: TEAL }}>Have a Question? Send Us an Inquiry</h2>
            <p className="mt-3 text-slate-600 leading-relaxed">Fill out the inquiry form or reach out directly via WhatsApp for immediate registration.</p>
            <div className="mt-6 rounded-2xl p-5" style={{ background: '#e7f4f2' }}>
              <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: TEAL }}>Prefer direct chat?</div>
              <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-2 text-2xl font-bold" style={{ color: TEAL, fontFamily: 'Georgia, serif' }} data-testid="inquiry-whatsapp-link">
                <Phone className="w-5 h-5" /> +91 8291188188
              </a>
            </div>
          </div>
          <div className="rounded-2xl p-6 sm:p-8" style={{ background: CREAM }}>
            {submitted ? (
              <div className="text-center py-6" data-testid="inquiry-thanks">
                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <CheckCircle className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Georgia, serif' }}>Inquiry Received</h3>
                <p className="mt-2 text-slate-600 text-sm">We&rsquo;ll reach out on WhatsApp within 24 hours.</p>
                <Button variant="outline" onClick={() => { setSubmitted(false); setForm({ name: '', phone: '', message: '', parent_role: 'Mother', child_age: '' }); }} className="mt-4">Send another</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="inquiry-form">
                <div>
                  <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Parent</label>
                  <select value={form.parent_role} onChange={(e) => setForm({ ...form, parent_role: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="inquiry-parent-role">
                    <option>Mother</option><option>Father</option><option>Guardian</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Parent Name</label>
                  <Input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="inquiry-name" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">WhatsApp Number *</label>
                  <Input placeholder="+91" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" data-testid="inquiry-phone" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Child&rsquo;s Age</label>
                  <select value={form.child_age} onChange={(e) => setForm({ ...form, child_age: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="inquiry-child-age">
                    <option value="">Select</option>
                    {[6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((a) => (<option key={a} value={a}>{a}</option>))}
                  </select>
                </div>
                <Button type="submit" disabled={submitting} className="w-full text-white rounded-full tracking-wider text-sm font-semibold h-12" style={{ background: 'linear-gradient(90deg,#f39c12,#e67e22)' }} data-testid="inquiry-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  SUBMIT INQUIRY
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
